import { useMemo } from 'react';
import type { TaxonomyField, TaxonomyRule } from '@/entities/taxonomy';
import type { TaxonomyRecord } from '@/entities/taxonomy-dataset';
import type { ResolvedList } from '@/entities/taxonomy-list';
import { applyRules, type EffectiveFields } from './applyRules';

export type EffectiveFieldsResolver = (record: TaxonomyRecord) => EffectiveFields;

/**
 * Per-record rule evaluation, memoised on the record object.
 *
 * Owned by the PAGE and handed to the grid, rather than living inside the grid,
 * because two consumers need the same answers: the grid for its markers and its
 * Save gate, and the export dialog for its row count. The dialog used to
 * recompute the lot — measured at ~310ms against ~30ms for a cache hit at the
 * 20 000-record cap — on the strength of a comment claiming the grid's cache was
 * unreachable from a page. It was not: `useRecordDraft` returns the record OBJECT
 * ITSELF for an unedited row, so both hold the same keys.
 *
 * That identity is the whole mechanism and the whole fragility. If the draft ever
 * returns fresh objects for untouched rows, this silently stops caching rather
 * than going wrong — slower, never incorrect.
 *
 * The deps are what INVALIDATE the map, not what the factory reads. A new schema,
 * a new rule set or a newly resolved list changes every answer, so the map is
 * thrown away wholesale; a single edit replaces one record object and misses only
 * that row.
 */
export function useEffectiveFields(
  fields: readonly TaxonomyField[],
  rules: readonly TaxonomyRule[],
  resolvedLists: Record<string, ResolvedList>,
): EffectiveFieldsResolver {
  const cache = useMemo(
    () => new WeakMap<TaxonomyRecord, EffectiveFields>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see the note above: these invalidate the map rather than being read
    [fields, rules, resolvedLists],
  );

  return useMemo(
    () => (record: TaxonomyRecord) => {
      const cached = cache.get(record);
      if (cached) {
        return cached;
      }
      const next = applyRules(fields, rules, record.cells, resolvedLists);
      cache.set(record, next);
      return next;
    },
    [cache, fields, rules, resolvedLists],
  );
}
